import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useFetch as useFetchV3 } from '@/modules/shared/composables/use-fetch'
import { useFetch } from '@/modules/core/composables/useFetch'
import { addItem, addItems, clearItems } from '@/modules/shared/utils/store'
import { CID } from '@/modules/shared/utils/store.types'
import { Money } from '@/modules/shared/utils/money'

///////////////////////////////////////////////////////////////////////////////
// Types
///////////////////////////////////////////////////////////////////////////////

export type Call = {
  id: string | null
  capital: Money
  investor_set_commitment_id: string | null
  investor_set_transaction_id: string | null
  commitment: {
    id: string | null
    capital: Money
    date: string
    investor: {
      _cid: string
      id: string | null
      name: string | null
    }
    commitment_remaining: Money
    ownership: number | null
  }
}

export type BatchCall = {
  id: number | null
  prior: boolean
  date: string | null
  schedule_date: string | null
  notes: string | null
  wiring_instructions: string | null
  selected_capital_type: '%' | '$'
  currency: string | null
  entered_capital_amount: number | null
  capital_call_transfers: Call[]
  fund_total_capital_committed: Money
}

///////////////////////////////////////////////////////////////////////////////
// Store
///////////////////////////////////////////////////////////////////////////////

type CapitalCallMap = Map<string, BatchCall>

export const useCapitalCallStore = defineStore('investing/capitalCallStore', () => {
  const route = useRoute()
  const baseUrl = computed(() => `/${route.params.slug}/investing`)

  const items = ref<CapitalCallMap>(new Map())
  const capital_calls = computed(() => Array.from(items.value.keys()).map((key) => items.value.get(key)))

  const fetchBatchCall = async (id: string, { slug }: { slug: string }) => {
    const data = await useFetch(`/v4/${slug}/investing/batch-calls/${id}`).get<BatchCall>()
    addItem(items, { ...data, _cid: data.id.toString() })
  }

  const addCapitalCall = async (capital_call) => {
    const { data, error } = await useFetchV3(
      `${baseUrl.value}/${capital_call.investable_type}/${capital_call.investable_id}/capital_call/add`,
    )
      .post({ capital_call })
      .json<{}>()

    if (error.value) {
      // TODO: handle error (e.g., display a message to the user)
      console.error(error.value)
      return
    }
  }

  const updateCapitalCall = async (capital_call) => {
    const { data, error } = await useFetchV3(
      `${baseUrl.value}/${capital_call.investable_type}/${capital_call.investable_id}/capital_call/update`,
    )
      .put({ capital_call })
      .json<{}>()

    if (error.value) {
      // TODO: handle error (e.g., display a message to the user)
      console.error(error.value)
      return
    }
  }

  const removeCapitalCall = async (id: string, { slug }: { slug: string }) => {
    await useFetch(`/v4/${slug}/investing/batch-calls/${id}/remove`).post()
  }

  const sendInvoiceReminders = async (id: string, { slug }: { slug: string }) => {
    await useFetch(`/v4/${slug}/investing/batch-calls/${id}/send-invoice-reminders`).post()
  }

  const issueCapitalCall = async (payload, { slug }: { slug: string }) => {
    await useFetch(`/v4/${slug}/investing/batch-calls/issue-capital-call`).post({ capital_call: payload })
  }

  return {
    items,
    capital_calls,

    fetchBatchCall,
    addCapitalCall,
    updateCapitalCall,
    removeCapitalCall,
    sendInvoiceReminders,
    issueCapitalCall,
  }
})
